// Generic formatting helpers for the COAC UI components.

/** Short "Xm/h/d ago" relative time from an ISO timestamp. */
export function relativeTime(iso: string): string {
    const t = new Date(iso).getTime();
    const now = new Date().getTime();
    const diffMs = Math.max(0, now - t);
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
}

/** en-GB short date (e.g. "05 Jan 2026") from an ISO string; em-dash if absent. */
export function formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Urgency of a response deadline relative to `now`, for the COAC deadline pill.
 * `iso` is the instant when the responses close (`responseClosesAt`).
 * UI-free (the consumer maps the level to a Pill colour) so it stays testable:
 *   passed  — the deadline has gone (overdue)
 *   urgent  — closes within 2 days
 *   soon    — closes within 7 days
 *   null    — no deadline, unparseable, or comfortably far out (> 7 days)
 * Mirrors the closing-soon window in the Desk summary counts (view-models.ts).
 */
export type CoacDeadlineRisk = 'passed' | 'urgent' | 'soon';

export function deadlineRisk(iso: string | null, now: Date): CoacDeadlineRisk | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const days = (t - now.getTime()) / (1000 * 60 * 60 * 24);
    if (days < 0) return 'passed';
    if (days <= 2) return 'urgent';
    if (days <= 7) return 'soon';
    return null;
}
